import { StatusGanttVariantType } from "@components/index";
import { StatusFilterType } from "@pages/Dashboard/contexts/Filter.context";

export interface StatusGanttResponse {
  text?: string;
  variant?: StatusGanttVariantType;
}

const status_not_started: StatusGanttResponse = {
  variant: "not-started",
  text: "Não iniciado",
};

const status_in_progress: StatusGanttResponse = {
  variant: "in-progress",
  text: "Em andamento",
};

const status_finished: StatusGanttResponse = {
  variant: "completed",
  text: "Finalizado",
};

const status_finished_late: StatusGanttResponse = {
  variant: "completed-late",
  text: "Finalizado com atraso",
};

const status_error: StatusGanttResponse = {
  variant: "error",
  text: "Erro",
};

const status_not_executed: StatusGanttResponse = {
  variant: "not-executed",
  text: "Não executado",
};

const status_awaiting_end: StatusGanttResponse = {
  variant: "awaiting-end",
  text: "Aguardando Fim",
};

const status_awaiting_justification: StatusGanttResponse = {
  variant: "awaiting-justification",
  text: "Aguardando justificativa",
};

const status_awaiting_finalize_justification: StatusGanttResponse = {
  variant: "awaiting-justification",
  text: "Aguardando Encerrar Justi.",
};

const statusMap: Record<Sentinel.Enumerators.ProcessExecutionStatus, StatusGanttResponse> = {
  NOT_STARTED: status_not_started,
  STARTED_EARLY: status_in_progress,
  STARTED: status_in_progress,
  STARTED_LATE: status_in_progress,
  DELAYED: status_not_started,
  COMPLETED_EARLY: status_finished,
  COMPLETED: status_finished,
  COMPLETED_LATE: status_finished_late,
  COMPLETED_LATE_AWAIT_JUSTIFICATION: status_awaiting_justification,
  COMPLETED_LATE_AWAIT_FINALIZE_JUSTIFICATION: status_awaiting_finalize_justification,
  ERROR: status_error,
  CANCELED: status_not_executed,
  AWAITING_END: status_awaiting_end,
  NON_EXECUTION: status_not_executed,
};

export function useStatusGantt(
  status?: Sentinel.Enumerators.ProcessExecutionStatus
): StatusGanttResponse {
  return statusMap[status] || status_not_started;
}

/**
 * Usado para filtrar eventos baseado no variant exibido
 * Exemplo:  "in-progress": ["STARTED", "STARTED_EARLY", "STARTED_LATE"],
 */
export function getStatusByVariant(): Record<StatusGanttVariantType, Sentinel.Enumerators.ProcessExecutionStatus[]> {
  const variantToStatus: Partial<Record<StatusGanttVariantType, Sentinel.Enumerators.ProcessExecutionStatus[]>> = {};

  Object.entries(statusMap).forEach(([status, response]: [string, StatusGanttResponse]) => {
    const variant = response.variant;
    if (variant) {
      if (!variantToStatus[variant]) {
        variantToStatus[variant] = [];
      }
      variantToStatus[variant].push(status as Sentinel.Enumerators.ProcessExecutionStatus);
    }
  });

  return variantToStatus as Record<StatusGanttVariantType, Sentinel.Enumerators.ProcessExecutionStatus[]>;
}

/**
 * Retorna as opções de status para filtros
 * value = variant, label = text
 */
export function getStatusFilterOptions(): Array<{ value: StatusFilterType; label: string }> {
  const variantToStatus = getStatusByVariant();
  const uniqueVariants = Object.keys(variantToStatus) as StatusGanttVariantType[];

  const variantOptions = uniqueVariants.map((variant) => {
    const firstStatus = variantToStatus[variant][0];
    const response = statusMap[firstStatus];
    return {
      value: variant,
      label: response.text || variant,
    };
  });

  return [
    ...variantOptions,
    {
      value: "late" as const,
      label: "Atrasado",
    },
  ];
}
